











































































































































import { Vue, Component, Prop, Watch } from "vue-property-decorator";
import PersonalData from "@/components/PersonalData.vue";
import Questions from "@/components/Questions.vue";
import Exclamation from "@/components/Icons/Exclamation.vue";

import { ValidationProvider, ValidationObserver } from "vee-validate";
import { extend } from "vee-validate";
// eslint-disable-next-line @typescript-eslint/camelcase
import { required, length, alpha_num } from "vee-validate/dist/rules";

import { DateTime } from "luxon";
import QRCode, { QRCodeToDataURLOptions } from "qrcode";

import countries from "i18n-iso-countries";
import State from "@/state";

// Override the default message.
extend("required", required);
extend("length", length);
extend("alpha_num", alpha_num);

export class ContactData {
  firstName: string | null;
  lastName: string | null;
  street: string | null;
  city: string | null;
  postalCode: string | null;
  countryCode: string | null;
  telephone: string | null;

  constructor() {
    this.firstName = null;
    this.lastName = null;
    this.street = null;
    this.city = null;
    this.postalCode = null;
    this.countryCode = "DE";
    this.telephone = null;
  }
}

export class QuestionHolder {
  respiratory: string | null;
  olfactory: string | null;
  contact: string | null;
  quarantine: string | null;
  riskArea: string | null;

  constructor() {
    this.respiratory = null;
    this.olfactory = null;
    this.contact = null;
    this.quarantine = null;
    this.riskArea = null;
  }
}

@Component({
  components: {
    PersonalData,
    Questions,
    ValidationProvider,
    ValidationObserver,
    Exclamation,
  },
})
export default class Form extends Vue {
  @Prop() readonly sourceState!: State;

  contactData = new ContactData();
  coronaPatID: string | null = null;
  questions = new QuestionHolder();
  qrDataURL: string | null = null;
  atLeastOneQuestionAnsweredWithYes = false;

  @Watch("contactData")
  contactDataChanged() {
    this.formChanged();
  }

  @Watch("coronaPatID")
  coronaPatIDChanged() {
    this.formChanged();
  }

  private formChanged() {
    this.qrDataURL = null;
    this.atLeastOneQuestionAnsweredWithYes = false;
  }

  generate() {
    this.generateQR(this.contactData, this.coronaPatID || "%%%%%", this.questions);
  }

  generateQR(contactData: ContactData, coronaPatID: string, questions: QuestionHolder) {
    if (this.anyQuestionYes(questions)) {
      this.atLeastOneQuestionAnsweredWithYes = true;
      return;
    } else {
      this.atLeastOneQuestionAnsweredWithYes = false;
    }
    const components: string[] = [];

    components.push(this.now());
    components.push(contactData.lastName?.trim() || "");
    components.push(contactData.firstName?.trim() || "");
    components.push(contactData.street?.trim() || "");
    components.push(`${contactData.postalCode || ""}`);
    components.push(contactData.city?.trim() || "");

    if (contactData.countryCode) {
      const countryCode3Digits = countries.alpha2ToAlpha3(contactData.countryCode.trim());
      components.push(countryCode3Digits);
    } else {
      components.push("");
    }
    components.push(contactData.telephone?.trim() || "");

    switch (this.sourceState) {
      case State.PatientHasCPID:
      case State.PatientHasNoCPID:
        components.push(`@${coronaPatID}`);
        break;
      default:
        components.push(coronaPatID);
        break;
    }

    components.push(questions.respiratory == "yes" || questions.olfactory == "yes" ? "J" : "N");
    components.push(questions.contact == "yes" || questions.quarantine == "yes" ? "J" : "N");
    components.push(questions.riskArea == "yes" ? "J" : "N");

    const qrContent = components.join(";");

    const options: QRCodeToDataURLOptions = {
      errorCorrectionLevel: "L",
      margin: 4,
      scale: 10,
    };

    QRCode.toDataURL(qrContent, options)
      .then((url: string) => {
        this.qrDataURL = url;
      })
      .then(() => {
        const qrCodeElement = document.getElementById("qrCode");
        qrCodeElement?.scrollIntoView();
      })
      .catch((err: Error) => {
        console.error(err);
      });
  }

  private get cpidRequired(): boolean {
    switch (this.sourceState) {
      case State.Visitor:
      case State.PatientHasCPID:
        return true;
      default:
        return false;
    }
  }

  private get showCPIDFormField(): boolean {
    switch (this.sourceState) {
      case State.Visitor:
      case State.PatientHasCPID:
      case State.Companion:
        return true;
      default:
        return false;
    }
  }

  // When the user is a patient AND has a CPID, only require first and last name.
  private get showOtherFormFields(): boolean {
    switch (this.sourceState) {
      case State.PatientHasCPID:
        return false;
      default:
        return true;
    }
  }

  private get oneYesAnswerInfoText(): string {
    switch (this.sourceState) {
      case State.Patient:
      case State.PatientHasCPID:
      case State.PatientHasNoCPID:
        return this.$t("form.questionsAnsweredWrongPatient").toString();
      default:
        return this.$t("form.questionsAnsweredWrong").toString();
    }
  }

  private print() {
    window.print();
  }

  private filename(): string {
    const timestamp = this.today;
    return this.$t("qr.filename", { timestamp }).toString();
  }

  private get today(): string {
    return DateTime.local().toFormat("dd. LLL yyyy");
  }

  private now(): string {
    return DateTime.local()
      .toUTC()
      .toFormat("yyyyLLddHHmm");
  }

  private anyQuestionYes(questions: QuestionHolder): boolean {
    return (
      questions.respiratory == null ||
      questions.respiratory == "yes" ||
      questions.olfactory == null ||
      questions.olfactory == "yes" ||
      questions.contact == null ||
      questions.contact == "yes" ||
      questions.quarantine == null ||
      questions.quarantine == "yes" ||
      questions.riskArea == null ||
      questions.riskArea == "yes"
    );
  }
}
